// src/api/client.ts
// Axios instance with JWT auth, silent token refresh, and error handling

import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';

const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

// ── TOKEN STORAGE ─────────────────────────────────────────────────
// Stored in localStorage for persistence across sessions
const KEYS = {
  ACCESS:  'ht_access_token',
  REFRESH: 'ht_refresh_token',
  USER_ID: 'ht_user_id',
};

export const tokenStore = {
  getAccess:  ()  => localStorage.getItem(KEYS.ACCESS),
  getRefresh: ()  => localStorage.getItem(KEYS.REFRESH),
  getUserId:  ()  => localStorage.getItem(KEYS.USER_ID),
  setTokens:  (access: string, refresh: string, userId: string) => {
    localStorage.setItem(KEYS.ACCESS,  access);
    localStorage.setItem(KEYS.REFRESH, refresh);
    localStorage.setItem(KEYS.USER_ID, userId);
  },
  updateAccess: (access: string) => {
    localStorage.setItem(KEYS.ACCESS, access);
  },
  clear: () => {
    localStorage.removeItem(KEYS.ACCESS);
    localStorage.removeItem(KEYS.REFRESH);
    localStorage.removeItem(KEYS.USER_ID);
  },
};

// ── JWT HELPERS ───────────────────────────────────────────────────

export function isTokenExpired(token: string): boolean {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    // Treat as expired if within 2 minutes of actual expiry
    return (payload.exp * 1000) - Date.now() < 2 * 60 * 1000;
  } catch {
    return true;
  }
}

// ── AXIOS INSTANCE ────────────────────────────────────────────────

const apiClient: AxiosInstance = axios.create({
  baseURL: BASE_URL,
  headers: { 'Content-Type': 'application/json' },
  timeout: 30000, // 30s — matches scoring Lambda timeout
});

// ── REQUEST INTERCEPTOR ───────────────────────────────────────────
// Attach JWT to every request. Silently refresh if expired.

let isRefreshing = false;
let refreshQueue: Array<(token: string) => void> = [];

apiClient.interceptors.request.use(async (config) => {
  // Skip auth for auth endpoints
  const publicPaths = ['/auth/login', '/auth/forgot-password', '/auth/reset-password'];
  if (publicPaths.some(p => config.url?.includes(p))) {
    return config;
  }

  let token = tokenStore.getAccess();

  if (token && isTokenExpired(token)) {
    if (!isRefreshing) {
      isRefreshing = true;
      try {
        const userId    = tokenStore.getUserId();
        const refreshTok = tokenStore.getRefresh();

        const response = await axios.post(`${BASE_URL}/auth/refresh`, {
          userId,
          refreshToken: refreshTok,
        });

        const newToken = response.data.data.accessToken;
        tokenStore.updateAccess(newToken);
        token = newToken;

        // Flush queued requests
        refreshQueue.forEach(cb => cb(newToken));
        refreshQueue = [];
      } catch {
        // Refresh failed — clear tokens, redirect to login
        tokenStore.clear();
        window.location.href = '/login';
        return Promise.reject(new Error('Session expired'));
      } finally {
        isRefreshing = false;
      }
    } else {
      // Queue this request while refresh is in progress
      token = await new Promise(resolve => {
        refreshQueue.push(resolve);
      });
    }
  }

  if (token) {
    config.headers['Authorization'] = `Bearer ${token}`;
  }

  return config;
});

// ── RESPONSE INTERCEPTOR ──────────────────────────────────────────
// Handle 401 globally — clear tokens and redirect to login

apiClient.interceptors.response.use(
  (response: AxiosResponse) => response,
  (error) => {
    if (error.response?.status === 401) {
      tokenStore.clear();
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export default apiClient;